import { useEffect, useRef } from "react";
import { isTimestampFromPastDay } from "../../../shared/utils/playbackHelpers";
import { shakaSetVideoPlayerControlState } from "../playerHelpers/shakaPlayerControls";
import { isVxOA9RestartStream, getContentPlayheadTime } from "../playerHelpers/playerSocuUtils";
import telusConvivaAnalytics from "../../../shared/utils/convivaAnalytics";
import PlayerConstants from "../../../shared/constants/player";

const { PLAYER_CONTROLS } = PlayerConstants;

export const useLivePlayerCoreEffect = (
  liveProgramDetails,
  playerMgr,
  propsRef,
  props,
  cdnUrl,
  isNextProgramNonRestartable
) => {
  const isNextProgramNonRestartableRef = useRef(isNextProgramNonRestartable);
  useEffect(() => {
    if (liveProgramDetails && playerMgr.current) {
      propsRef.current = { ...props, cdnUrl: cdnUrl.current };
      telusConvivaAnalytics.onPlayNextLive(propsRef.current, playerMgr.current);
    }
    // onPlayNextLive should only be triggered when liveProgramDetails changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveProgramDetails]);

  useEffect(() => {
    isNextProgramNonRestartableRef.current = isNextProgramNonRestartable;
  }, [isNextProgramNonRestartable]);

  return { isNextProgramNonRestartableRef };
};

export const useLivePlaybackEffect = (contentType, isSourceLoaded, livePlaybackCanStart, playbackError, playerMgr) => {
  // Side effect to control live playback a/v output
  useEffect(() => {
    if (contentType === "LIVE" && isSourceLoaded) {
      if (!livePlaybackCanStart || playbackError?.code || playbackError?.message) {
        if (playerMgr.current && !playerMgr.current?.destroyed) {
          playerMgr.current.pause();
        }
      } else {
        if (playerMgr.current && !playerMgr.current?.destroyed) {
          playerMgr.current.play().catch((error) => {
            // This error handler was added to handle the promise rejection from this issue: https://developer.chrome.com/blog/play-request-was-interrupted/
            // TODO: Handle the pause/play and load/play race conditions properly when refactoring the player
            console.error(error);
          });
        }
      }
    }
  }, [contentType, isSourceLoaded, livePlaybackCanStart, playbackError, playerMgr]);
};

export const useLiveProgressEffect = (
  endTime,
  startTime,
  currentPlaybackTime,
  checkIfPauseLiveAllowed,
  playerMgr,
  hasNextProgramStarted,
  landTime,
  setPlayPauseIcon,
  leftMargin,
  shakaHlsLandTimeSeekHead
) => {
  useEffect(() => {
    if (checkIfPauseLiveAllowed()) {
      if (landTime.current === null && currentPlaybackTime > 0 && !isTimestampFromPastDay(currentPlaybackTime)) {
        const initialLandTime = Math.max(currentPlaybackTime, startTime);
        landTime.current = initialLandTime;
        shakaHlsLandTimeSeekHead.current = playerMgr.current?.getCurrentTime();
        /**
         * When live program changes, we need to make sure the new program progress is initiated at 0. This will prevent progress toolkit from shifting back
         */
        leftMargin.current = hasNextProgramStarted.current
          ? 0
          : ((currentPlaybackTime - startTime) / (endTime - startTime)) * 100;
      } else {
        /**
         * Same as above comment, to prevent progress toolkit from shifting back
         */
        // This address the potential issue where end time and start time is update before we can actually update the landTime
        if (landTime.current !== null && landTime.current < startTime) {
          landTime.current = startTime;
        }
        leftMargin.current = hasNextProgramStarted.current
          ? 0
          : landTime.current !== null
          ? ((landTime.current - startTime) / (endTime - startTime)) * 100
          : 0;
      }
      hasNextProgramStarted.current = false;
      const updatedCurrentPlaybackTime = isVxOA9RestartStream(playerMgr)
        ? getContentPlayheadTime(playerMgr)
        : currentPlaybackTime;
      if (
        !playerMgr.current?.destroyed &&
        playerMgr.current?.isPaused() &&
        landTime.current !== null &&
        updatedCurrentPlaybackTime <= landTime.current
      ) {
        landTime.current = currentPlaybackTime;
        shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.RESUME);
        setPlayPauseIcon("/images/playback-pause.svg");
      }
    }
  }, [
    endTime,
    startTime,
    currentPlaybackTime,
    checkIfPauseLiveAllowed,
    playerMgr,
    hasNextProgramStarted,
    landTime,
    setPlayPauseIcon,
    leftMargin,
    shakaHlsLandTimeSeekHead,
  ]);
};

export const useJumpToLiveEffect = (
  hasJumpedToLive,
  playerMgr,
  showLiveProgress,
  landTime,
  startTime,
  hasNextProgramStarted
) => {
  useEffect(() => {
    if (hasJumpedToLive.current) {
      if (!playerMgr.current?.destroyed && playerMgr.current?.isPaused()) {
        showLiveProgress.current = false;
      }
      landTime.current = startTime;
      hasJumpedToLive.current = false;
      hasNextProgramStarted.current = false;
    }
  }, [startTime, hasJumpedToLive, playerMgr, showLiveProgress, landTime, hasNextProgramStarted]);
};
